import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';

import { User } from '@ninety/platform/models/_shared/user';
import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { Company } from '@ninety/platform/models/company/company';
import { EmailType } from '@ninety/platform/models/directory/email-type';
import { environment } from '@ninety/ui/web/environments';

import { COMPANY_READER, CompanyReader } from '../tokens/company-reader.token';
import { CURRENT_USER_READER, CurrentUserReader } from '../tokens/current-user-reader.token';
import { DatadogRumService, DatadogRumUser, DatadogRumAccount } from '../vendor/datadog/datadog-rum.service';

/** Payload emitted when the RUM session has been linked to FullStory; mapped to a Segment track action. */
export interface DatadogSessionLinkedEvent {
  event: SegmentTrackEvent;
  params: { dd_rum_session_url: string };
}

type FullStoryObjectApi = {
  setVars?: (scope: string, vars: Record<string, string>) => void;
  getCurrentSessionURL?: (now?: boolean) => string | undefined;
};

type FullStoryFunctionApi = ((cmd: string, opts?: { format?: string }) => Promise<string | undefined>) &
  FullStoryObjectApi;

type FullStoryGlobal = FullStoryObjectApi | FullStoryFunctionApi;

// Syncs user/company to RUM (setUser/setAccount) and cross-links session with FullStory.
@Injectable()
export class DatadogRumUserSyncService {
  private readonly currentUserReader = inject<CurrentUserReader>(CURRENT_USER_READER);
  private readonly companyReader = inject<CompanyReader>(COMPANY_READER);

  private readonly destroyRef = inject(DestroyRef);
  private readonly rum = inject(DatadogRumService);
  private fullStoryObserverRegistered = false;
  private fullStorySyncDone = false;
  /** The RUM session id we last sent to FullStory as a page property. */
  private fullStoryPagePropSentForSession: string | null = null;
  private crossLinkRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private crossLinkRetryCount = 0;

  // ReplaySubject(1): this is a one-shot bridge into a platform-state effect. If the link event
  // fires before EffectsRunner subscribes, a plain Subject would drop it; replaying the last value
  // keeps the inversion behavior-neutral vs the old direct dispatch.
  private readonly _sessionLinked$ = new ReplaySubject<DatadogSessionLinkedEvent>(1);
  /**
   * Emits once the RUM session has been cross-linked to FullStory. A `platform/state` effect
   * (`SegmentEffects`) subscribes and dispatches `SegmentActions.track`, so this service no longer
   * depends on `platform/state` (DEV-26105 PR4).
   */
  readonly sessionLinked$: Observable<DatadogSessionLinkedEvent> = this._sessionLinked$.asObservable();

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.crossLinkRetryTimer) clearTimeout(this.crossLinkRetryTimer);
    });
    combineLatest([this.currentUserReader.currentUser$, this.companyReader.company$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, company]) => {
        const rumUser = this.toRumUser(user);
        const rumAccount = this.toRumAccount(company);
        this.rum.setUser(rumUser);
        this.rum.setAccount(rumAccount);
        if (!rumUser) {
          this.fullStorySyncDone = false;
          this.fullStoryPagePropSentForSession = null;
          this.crossLinkRetryCount = 0;
        }
        if (!environment.production && (rumUser || rumAccount)) {
          // eslint-disable-next-line no-console
          console.debug('Datadog RUM user/account synced', { rumUser, rumAccount });
        }
        if (rumUser && rumAccount) {
          this.crossLinkSessionWithFullStory();
        }
      });
  }

  /** Build a direct Datadog RUM Explorer URL for the given session. */
  private buildRumSessionUrl(sessionId: string): string {
    return `https://app.datadoghq.com/rum/explorer?query=%40session.id%3A${sessionId}`;
  }

  /**
   * Sets dd_rum_session_url on FullStory page (v2 setProperties + v1 setVars).
   * Returns true if the property was set, false on error.
   */
  private setFullStoryPageRumLink(fs: FullStoryGlobal, ddSessionId: string): boolean {
    try {
      const pageProps = { dd_rum_session_url: this.buildRumSessionUrl(ddSessionId) };
      if (typeof fs === 'function') {
        (fs as (cmd: string, opts: { type: string; properties: Record<string, string> }) => void)('setProperties', {
          type: 'page',
          properties: pageProps,
        });
      }
      fs.setVars?.('page', pageProps);
      return true;
    } catch {
      return false;
    }
  }

  private crossLinkSessionWithFullStory(): void {
    const win = typeof window !== 'undefined' ? (window as { FS?: FullStoryGlobal }) : undefined;
    const fs = win?.FS;
    const ddSessionId = this.rum.getSessionId();

    if (!fs || !this.rum.isInitialized() || ddSessionId == null) {
      this.scheduleCrossLinkRetry();
      return;
    }

    if (this.fullStorySyncDone && this.fullStoryPagePropSentForSession === ddSessionId) return;

    // FullStory → RUM: set page property once per session id (with a single retry)
    if (this.fullStoryPagePropSentForSession !== ddSessionId) {
      const sent = this.setFullStoryPageRumLink(fs, ddSessionId);
      if (sent) {
        this.fullStoryPagePropSentForSession = ddSessionId;
        this._sessionLinked$.next({
          event: SegmentTrackEvent.DD_RUM_SESSION_LINKED,
          params: { dd_rum_session_url: this.buildRumSessionUrl(ddSessionId) },
        });
      } else {
        // Single retry after 3s in case FullStory wasn't ready
        setTimeout(() => {
          if (this.fullStoryPagePropSentForSession === ddSessionId) return;
          if (this.setFullStoryPageRumLink(fs, ddSessionId)) {
            this.fullStoryPagePropSentForSession = ddSessionId;
            this._sessionLinked$.next({
              event: SegmentTrackEvent.DD_RUM_SESSION_LINKED,
              params: { dd_rum_session_url: this.buildRumSessionUrl(ddSessionId) },
            });
          }
        }, 3000);
      }
    }

    // RUM → FullStory: one-time per login, sync FullStory URL/session id to RUM
    if (this.fullStorySyncDone) return;

    try {
      const url = fs.getCurrentSessionURL?.();
      if (url) {
        this.rum.setGlobalContextProperty('fullstory_session_url', url);
        this.fullStorySyncDone = true;
      } else {
        const maxAttempts = 5;
        const intervalMs = 2000;
        let attempts = 0;
        const tryUrl = (): void => {
          attempts += 1;
          const retryUrl = fs.getCurrentSessionURL?.();
          if (retryUrl) {
            this.rum.setGlobalContextProperty('fullstory_session_url', retryUrl);
            this.fullStorySyncDone = true;
            return;
          }
          if (attempts < maxAttempts) setTimeout(tryUrl, intervalMs);
        };
        setTimeout(tryUrl, intervalMs);
      }
    } catch {
      // ignore
    }

    if (typeof fs === 'function' && !this.fullStoryObserverRegistered) {
      this.fullStoryObserverRegistered = true;
      try {
        (fs as (cmd: string, opts: unknown) => unknown)('observe', {
          type: 'start',
          callback: () => {
            const url = fs.getCurrentSessionURL?.();
            if (url) {
              this.rum.setGlobalContextProperty('fullstory_session_url', url);
              this.fullStorySyncDone = true;
            }
          },
        });
      } catch {
        // ignore
      }
    }

    // getSessionAsync is lightweight and idempotent, safe on re-entry
    if (typeof fs === 'function') {
      try {
        (fs as (cmd: string, opts?: { format?: string }) => Promise<string | undefined>)('getSessionAsync', {
          format: 'id',
        })
          ?.then?.((id: string | undefined) => {
            if (id) this.rum.setGlobalContextProperty('fullstory_session_id', id);
          })
          ?.catch?.(() => undefined);
      } catch {
        // ignore
      }
    }
  }

  /** Retry crossLinkSessionWithFullStory if FS or RUM wasn't ready yet (e.g. first page load). */
  private scheduleCrossLinkRetry(): void {
    const maxRetries = 10;
    const retryIntervalMs = 2000;
    if (this.crossLinkRetryTimer || this.crossLinkRetryCount >= maxRetries) return;
    this.crossLinkRetryCount += 1;
    this.crossLinkRetryTimer = setTimeout(() => {
      this.crossLinkRetryTimer = null;
      this.crossLinkSessionWithFullStory();
    }, retryIntervalMs);
  }

  private toRumUser(user: User | undefined | null): DatadogRumUser | null {
    if (!user) return null;
    const id = user._id ?? user.personId ?? '';
    if (!id) return null;
    const name = user.fullName ?? undefined;
    const email =
      user.primaryEmail ??
      user.emailAddresses?.find(e => e.typeCode === EmailType.main)?.email ??
      user.emailAddresses?.[0]?.email;
    const rumUser: DatadogRumUser = {
      id,
      name: name ?? undefined,
      email: email ?? undefined,
      personId: user.personId ?? undefined,
      companyId: user.company?.companyId ?? undefined,
    };
    return rumUser;
  }

  private toRumAccount(company: Company | undefined | null): DatadogRumAccount | null {
    if (!company) return null;
    const id = company._id ?? company.companyId ?? '';
    if (!id) return null;
    return {
      id,
      name: company.name ?? undefined,
      companyId: company.companyId ?? undefined,
    };
  }
}
